import { Search } from '@mui/icons-material';
import { Button, Checkbox, Divider, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import ModalV2 from '/@/components/bayer/modal-v2';
import ChipLabel from '/@/components/chip-label';
import { useMyFields } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import { getCrops } from '/@/views/dashboard/hooks/crops';

const FieldSelectorModal = ({
    text,
    field,
    userNodeId,
    ModalV2Props,
    action,
    lockedFieldIds,
    searchPlaceholder,
    syncSelectionWithUrl = true,
}: {
    text: string;
    field?: Field;
    userNodeId: number;
    // The modal renders its own content
    ModalV2Props: Omit<React.ComponentProps<typeof ModalV2>, 'children'>;
    action: {
        onClick: (fieldIds: number[]) => void;
        text: string;
    };
    /** Fields shown checked and disabled (e.g. the ones already in the visit report); never returned. */
    lockedFieldIds?: number[];
    /** Search placeholder; defaults to the generic one. */
    searchPlaceholder?: string;
    /** Pre-select (and then clear) the `fieldIds` URL param. Off where that param means something else. */
    syncSelectionWithUrl?: boolean;
}) => {
    const [search, setSearch] = useState('');
    const [selectedFieldIdsSet, setSelectedFieldIdsSet] = useState<Set<number>>(new Set());
    const country = useSelector(getSessionCountry);
    const [searchParams, setSearchParams] = useSearchParams();
    const hasProcessedFieldIds = useRef(false);

    // Pre-select field from URL params (when returning from field creation)
    const fieldIdsFromUrl = searchParams.get('fieldIds');
    useEffect(() => {
        if (syncSelectionWithUrl && fieldIdsFromUrl && ModalV2Props.open && !hasProcessedFieldIds.current) {
            hasProcessedFieldIds.current = true;
            const fieldIds = fieldIdsFromUrl.split(',').map(Number).filter(Boolean);
            if (fieldIds.length > 0) {
                setSelectedFieldIdsSet(new Set(fieldIds));
                // Clear the fieldIds param to avoid re-selecting on subsequent opens
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('fieldIds');
                setSearchParams(newParams, { replace: true });
            }
        }
    }, [syncSelectionWithUrl, fieldIdsFromUrl, ModalV2Props.open, searchParams, setSearchParams]);

    const fieldsQuery = useMyFields({
        page: 1,
        pageSize: 10000,
        name: search,
        country: country.code,
        selectOnly: 'id,name,location,active_crop_summary',
        userNodeId,
        includeChildren: false,
    });

    const crops = getCrops({ size: 'xxs' });
    const fields: Field[] = fieldsQuery.data?.results ?? [];
    const isLocked = (fieldId: number) => Boolean(lockedFieldIds?.includes(fieldId));

    useEffect(() => {
        if (field && ModalV2Props.open) {
            setSelectedFieldIdsSet(new Set([field.id]));
        }
    }, [field, ModalV2Props.open]);

    const toggleFieldSelection = (fieldId: number) => {
        setSelectedFieldIdsSet((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(fieldId)) {
                newSet.delete(fieldId);
            } else {
                newSet.add(fieldId);
            }
            return newSet;
        });
    };

    return (
        <ModalV2
            actions={
                <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ width: '100%' }}>
                    <Button
                        variant='contained'
                        color='secondary'
                        disabled={selectedFieldIdsSet.size === 0}
                        onClick={() => {
                            action.onClick(Array.from(selectedFieldIdsSet));
                        }}
                    >
                        {action.text}
                    </Button>
                </Stack>
            }
            {...ModalV2Props}
        >
            <Stack spacing={2} width='100%'>
                <Typography variant='subtitle1' color='primary' textAlign='left'>
                    {text}
                </Typography>
                <TextField
                    InputProps={{
                        startAdornment: <Search />,
                    }}
                    placeholder={searchPlaceholder ?? _('Busca lotes por nombre')}
                    onChange={(e) => setSearch(e.target.value)}
                    value={search}
                    fullWidth
                />
                <Stack className='mp-mask' divider={<Divider />} width='100%'>
                    {fieldsQuery.isValidating ? (
                        <Stack direction='column' spacing={1}>
                            {Array.from({ length: 5 }, (_, index) => (
                                <Skeleton variant='rectangular' height={50} key={`skeleton-visit-${index}`} />
                            ))}
                        </Stack>
                    ) : (
                        fields.map((f: Field) => {
                            const cropName = crops[f.activeCropMonitoringCrop || 'fallow']?.name;
                            const cropIcon = crops[f.activeCropMonitoringCrop || 'fallow']?.icon;
                            const locked = isLocked(f.id);
                            return (
                                <Stack
                                    direction='row'
                                    key={`field-${f.id}`}
                                    onClick={locked ? undefined : () => toggleFieldSelection(f.id)}
                                    aria-disabled={locked || undefined}
                                    sx={[
                                        {
                                            cursor: locked ? 'default' : 'pointer',
                                            '&:hover': {
                                                backgroundColor: locked ? undefined : 'action.hover',
                                            },
                                        },
                                        (locked || selectedFieldIdsSet.has(f.id)) && {
                                            backgroundColor: 'action.selected',
                                        },
                                    ]}
                                >
                                    <Checkbox
                                        color='secondary'
                                        value={f.id}
                                        checked={locked || selectedFieldIdsSet.has(f.id)}
                                        disabled={locked}
                                        readOnly
                                    />
                                    <Stack
                                        spacing={0.5}
                                        px={1.5}
                                        py={1}
                                        justifyContent='flex-start'
                                        direction='column'
                                        alignItems='start'
                                        textAlign='left'
                                    >
                                        <Stack
                                            direction='row'
                                            spacing={1}
                                            useFlexGap
                                            flexWrap='wrap'
                                            alignItems='center'
                                        >
                                            <Typography
                                                variant='subtitle2'
                                                color={locked ? 'text.disabled' : 'primary'}
                                                sx={{ textAlign: 'left' }}
                                            >
                                                {f.name}
                                            </Typography>
                                            <ChipLabel
                                                label={`${cropName} ${f.activeCropMonitoringIsGenericHybrid ? _('Híbrido Genérico') : f.activeCropMonitoringProductName || ''}`}
                                                type={f.activeCropMonitoringCrop || 'fallow'}
                                                icon={cropIcon}
                                                size='xs'
                                            />
                                        </Stack>
                                        {f.location && (
                                            <Typography
                                                variant='caption'
                                                color={locked ? 'text.disabled' : 'primary.mui.inactive'}
                                            >
                                                {f.location}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            );
                        })
                    )}
                </Stack>
            </Stack>
        </ModalV2>
    );
};

export default FieldSelectorModal;
